import Badge from '../shared/Badge';

/**
 * Theme Preview Component
 *
 * Live preview of theme colors showing various UI elements
 * Displays how the theme will look in practice
 */
export default function ThemePreview({ theme }) {
  if (!theme || !theme.colors) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <p>No theme to preview</p>
      </div>
    );
  }

  const { colors } = theme;

  return (
    <div
      className="h-full overflow-y-auto rounded-lg border-2"
      style={{
        backgroundColor: colors.background.primary,
        borderColor: colors.border.default
      }}
    >
      {/* Header Preview */}
      <div
        className="p-4 border-b"
        style={{
          backgroundColor: colors.background.secondary,
          borderColor: colors.border.default
        }}
      >
        <h3
          className="text-lg font-bold mb-2"
          style={{ color: colors.text.primary }}
        >
          Theme Preview
        </h3>
        <p
          className="text-sm"
          style={{ color: colors.text.secondary }}
        >
          This is how your theme will look
        </p>
      </div>

      <div className="p-4 space-y-6">
        {/* Text Samples */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Text Colors
          </h4>
          <div className="space-y-2">
            <p style={{ color: colors.text.primary }}>Primary text color</p>
            <p style={{ color: colors.text.secondary }}>Secondary text color</p>
            <p style={{ color: colors.text.muted }}>Muted text color</p>
            <p style={{ color: colors.text.disabled }}>Disabled text color</p>
          </div>
        </div>

        {/* Background Samples */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Background Colors
          </h4>
          <div className="space-y-2">
            <div
              className="p-3 rounded-lg"
              style={{
                backgroundColor: colors.background.secondary,
                color: colors.text.primary
              }}
            >
              Secondary Background
            </div>
            <div
              className="p-3 rounded-lg"
              style={{
                backgroundColor: colors.background.tertiary,
                color: colors.text.primary
              }}
            >
              Tertiary Background
            </div>
            <div
              className="p-3 rounded-lg"
              style={{
                backgroundColor: colors.background.elevated,
                color: colors.text.primary
              }}
            >
              Elevated Background (Cards/Modals)
            </div>
          </div>
        </div>

        {/* Button Samples */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Buttons
          </h4>
          <div className="flex flex-wrap gap-2">
            <button
              className="px-4 py-2 rounded-lg font-medium transition-all"
              style={{
                backgroundColor: colors.accent.primary,
                color: '#ffffff'
              }}
            >
              Primary Button
            </button>
            <button
              className="px-4 py-2 rounded-lg font-medium transition-all border"
              style={{
                backgroundColor: colors.background.secondary,
                borderColor: colors.border.default,
                color: colors.text.primary
              }}
            >
              Secondary Button
            </button>
            <button
              className="px-4 py-2 rounded-lg font-medium opacity-50 cursor-not-allowed"
              style={{
                backgroundColor: colors.disabled,
                color: colors.text.disabled
              }}
            >
              Disabled Button
            </button>
          </div>
        </div>

        {/* Semantic Colors */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Semantic Colors
          </h4>
          <div className="space-y-2">
            <div
              className="px-3 py-2 rounded-lg flex items-center gap-2"
              style={{
                backgroundColor: `${colors.success}20`,
                borderLeft: `4px solid ${colors.success}`
              }}
            >
              <span style={{ color: colors.success }}>✓</span>
              <span style={{ color: colors.text.primary }}>Success message</span>
            </div>
            <div
              className="px-3 py-2 rounded-lg flex items-center gap-2"
              style={{
                backgroundColor: `${colors.warning}20`,
                borderLeft: `4px solid ${colors.warning}`
              }}
            >
              <span style={{ color: colors.warning }}>⚠</span>
              <span style={{ color: colors.text.primary }}>Warning message</span>
            </div>
            <div
              className="px-3 py-2 rounded-lg flex items-center gap-2"
              style={{
                backgroundColor: `${colors.error}20`,
                borderLeft: `4px solid ${colors.error}`
              }}
            >
              <span style={{ color: colors.error }}>✕</span>
              <span style={{ color: colors.text.primary }}>Error message</span>
            </div>
            <div
              className="px-3 py-2 rounded-lg flex items-center gap-2"
              style={{
                backgroundColor: `${colors.info}20`,
                borderLeft: `4px solid ${colors.info}`
              }}
            >
              <span style={{ color: colors.info }}>ℹ</span>
              <span style={{ color: colors.text.primary }}>Info message</span>
            </div>
          </div>
        </div>

        {/* Borders */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Borders
          </h4>
          <div className="space-y-2">
            <div
              className="p-3 rounded-lg border"
              style={{
                borderColor: colors.border.subtle,
                color: colors.text.primary
              }}
            >
              Subtle border
            </div>
            <div
              className="p-3 rounded-lg border-2"
              style={{
                borderColor: colors.border.default,
                color: colors.text.primary
              }}
            >
              Default border
            </div>
            <div
              className="p-3 rounded-lg border-2"
              style={{
                borderColor: colors.border.accent,
                color: colors.text.primary
              }}
            >
              Accent border
            </div>
          </div>
        </div>

        {/* Form Elements */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Form Elements
          </h4>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Text input..."
              className="w-full px-3 py-2 rounded-lg border focus:outline-none"
              style={{
                backgroundColor: colors.background.secondary,
                borderColor: colors.border.default,
                color: colors.text.primary
              }}
            />
            <select
              className="w-full px-3 py-2 rounded-lg border"
              style={{
                backgroundColor: colors.background.secondary,
                borderColor: colors.border.default,
                color: colors.text.primary
              }}
            >
              <option>Select option...</option>
              <option>Option 1</option>
              <option>Option 2</option>
            </select>
            <textarea
              placeholder="Textarea..."
              rows="3"
              className="w-full px-3 py-2 rounded-lg border resize-none"
              style={{
                backgroundColor: colors.background.secondary,
                borderColor: colors.border.default,
                color: colors.text.primary
              }}
            />
          </div>
        </div>

        {/* Gradient Preview */}
        <div>
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: colors.text.primary }}
          >
            Gradients
          </h4>
          <div
            className="h-24 rounded-lg"
            style={{
              background: `linear-gradient(to right, ${colors.gradient.from}, ${colors.gradient.via}, ${colors.gradient.to})`
            }}
          />
          <div
            className="h-24 rounded-lg mt-2"
            style={{
              background: `linear-gradient(to right, ${colors.gradient.accent.from}, ${colors.gradient.accent.to})`
            }}
          />
        </div>
      </div>
    </div>
  );
}
